import { forwardRef, useCallback, useMemo } from "react";
import type { ComponentPropsWithRef, MouseEventHandler } from "react";
import { Link, Section } from "@react-email/components";
import type { StyleValue } from "@/react/components/shared/BuilderUI/StyleValue.types";
import type { BlockComponent } from "@circle-react-shared/BuilderUI/BuilderUI.types";
import { useParseStyles } from "../../../Design/useParseStyles";
import { defaultStyles } from "./defaultStyles";

const { margin, padding, ...defaultStylesWithoutMarginAndPadding } =
  defaultStyles;

const sectionDefaultStyles = {
  margin,
  padding,
} as const;

const inlineDefaultStyles = defaultStylesWithoutMarginAndPadding;

interface LinkBlockProps extends ComponentPropsWithRef<typeof Section> {
  "data-preview-mode": boolean;
  href: string;
  styles: Record<string, StyleValue>;
  text: string;
}

export const LinkBlock: BlockComponent = forwardRef<
  HTMLTableElement,
  LinkBlockProps
>(
  (
    { text, href, "data-preview-mode": isPreviewMode, styles, ...rest },
    ref,
  ) => {
    const sectionStyles = useParseStyles({
      styles: useMemo(() => {
        const {
          margin: marginStyles,
          padding: paddingStyles,
          sizing: sizingStyles,
          textAlign: textAlignStyles,
        } = styles;
        return {
          margin: marginStyles,
          padding: paddingStyles,
          sizing: sizingStyles,
          textAlign: textAlignStyles,
        };
      }, [styles]),
      defaultStyles: sectionDefaultStyles,
    });
    const inlineStyles = useParseStyles({
      styles: useMemo(() => {
        const {
          // eslint-disable-next-line sonarjs/no-unused-vars, @typescript-eslint/no-unused-vars -- we need to destructure the styles object to get the other styles
          margin: _marginStyles,
          // eslint-disable-next-line sonarjs/no-unused-vars, @typescript-eslint/no-unused-vars -- we need to destructure the styles object to get the other styles
          padding: _paddingStyles,
          // eslint-disable-next-line sonarjs/no-unused-vars, @typescript-eslint/no-unused-vars -- we need to destructure the styles object to get the other styles
          sizing: _sizingStyles,
          // eslint-disable-next-line sonarjs/no-unused-vars, @typescript-eslint/no-unused-vars -- we need to destructure the styles object to get the other styles
          textAlign: _textAlignStyles,
          ...restStyles
        } = styles;
        return restStyles;
      }, [styles]),
      defaultStyles: inlineDefaultStyles,
    });

    const handleClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
      event => {
        if (isPreviewMode) event.preventDefault();
      },
      [isPreviewMode],
    );

    return (
      <Section ref={ref} style={sectionStyles} {...rest}>
        <Link
          href={href}
          target="_blank"
          onClick={handleClick}
          rel="noopener"
          style={inlineStyles}
        >
          {text}
        </Link>
      </Section>
    );
  },
);

LinkBlock.displayName = "LinkBlock";
